import React from 'react';
import { DBTable } from '../components/DBTable';
import { supabase } from './supabase';

export interface DBHelperParams {
  tableName: string;
  schema?: string;
  whereClause?: string;
  columns?: string[];
  pinnedColumns?: number;
  columnLabels?: Record<string, string>;
  sortable?: boolean;
  searchable?: boolean;
  filterable?: boolean;
  selectable?: boolean;
  pageSize?: number;
  initialPage?: number;
  limit?: number;
  offset?: number;
  title?: string;
  onEdit?: (row: any) => void;
  onAdd?: () => void;
  onAction?: (action: string, selectedIds: (string | number)[], state: 'download' | 'upload') => void;
  onRowDoubleClick?: (row: any) => void;
}

/**
 * Store globale per la mappatura delle configurazioni utente.
 * Viene popolato da useConfig per un accesso rapido ovunque.
 */
let configMap: Map<string, string> = new Map();

/**
 * Helper per recuperare un valore di configurazione dal map globale.
 * @param key Chiave della configurazione
 * @param fallback Valore di default se non trovato
 */
export const getConfigValue = (key: string, fallback: string = ''): string => {
  return configMap.get(key) || fallback;
};

/**
 * Aggiorna il map globale delle configurazioni partendo dalla struttura UserConfig.
 */
export const updateConfigMap = (config: any) => {
  const newMap = new Map<string, string>();
  
  const flattenNode = (node: any, parentName: string = '') => {
    if (!node) return;
    
    const currentItemName = node.item || parentName;
    
    // Se il nodo ha parametri diretti (non children)
    Object.entries(node).forEach(([key, value]) => {
      if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'object' && 'fieldType' in value[0]) {
        const field = value[0];
        const val = field.userValue !== undefined && field.userValue !== "" ? field.userValue : field.defaultValue;
        
        // Mappatura piatta semplice per chiavi globali
        newMap.set(key, val);
        
        // Mappatura specifica per sezione se itemName esiste
        if (currentItemName) {
          const namespacedKey = `${currentItemName.toLowerCase().replace(/\s+/g, '_')}_${key}`;
          newMap.set(namespacedKey, val);
        }
      }
    });

    if (node.children) {
      node.children.forEach((child: any) => flattenNode(child, currentItemName));
    }
  };

  if (config && config.items) {
    config.items.forEach((item: any) => flattenNode(item));
  }
  
  configMap = newMap;
  // console.log('dbHelper: Config Map aggiornata.', Array.from(configMap.keys()));
};

/**
 * Routine di helper per il controllo congruenza dell'account durante il login.
 * Verifica che l'email sia presente con id_tipo_mail = 99 e che la persona esista.
 * Chiamata lato server per scavalcare le policy RLS.
 */
export const controllo_congruenza_account = async (email: string, authUuid: string) => {
  try {
    const response = await fetch('/api/auth/check-congruence', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, authUuid })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Errore durante la chiamata server');
    }

    return await response.json();
  } catch (err: any) {
    console.error('dbHelper: Errore in controllo_congruenza_account:', err);
    return { success: false, errorType: 'SERVER_ERROR', message: `Errore comunicazione server: ${err.message}` };
  }
};

/**
 * Routine di helper per la gestione CRUD di tabelle SQL.
 * @param params Parametri di configurazione della griglia
 * @returns Componente React per la visualizzazione della tabella
 */
export const dbHelper = (params: DBHelperParams) => {
  return <DBTable {...params} />;
};

/**
 * Routine di verifica Codice Fiscale Italiano
 * Implementa il controllo formale e il check digit
 */
export const validaCodiceFiscale = (cf: string): boolean => {
  if (!cf) return false;
  const cfUpper = cf.toUpperCase().trim();
  if (cfUpper.length !== 16) return false;
  
  // RegEx formale
  const cfRegEx = /^[A-Z]{6}[0-9]{2}[A-EHLMPR-T][0-9]{2}[A-Z][0-9]{3}[A-Z]$/;
  if (!cfRegEx.test(cfUpper)) return false;
  
  // Algoritmo Check Digit
  const setPari: Record<string, number> = {
    '0': 0, '1': 1, '2': 2, '3': 3, '4': 4, '5': 5, '6': 6, '7': 7, '8': 8, '9': 9,
    'A': 0, 'B': 1, 'C': 2, 'D': 3, 'E': 4, 'F': 5, 'G': 6, 'H': 7, 'I': 8, 'J': 9,
    'K': 10, 'L': 11, 'M': 12, 'N': 13, 'O': 14, 'P': 15, 'Q': 16, 'R': 17, 'S': 18,
    'T': 19, 'U': 20, 'V': 21, 'W': 22, 'X': 23, 'Y': 24, 'Z': 25
  };
  
  const setDispari: Record<string, number> = {
    '0': 1, '1': 0, '2': 5, '3': 7, '4': 9, '5': 13, '6': 15, '7': 17, '8': 19, '9': 21,
    'A': 1, 'B': 0, 'C': 5, 'D': 7, 'E': 9, 'F': 13, 'G': 15, 'H': 17, 'I': 19, 'J': 21,
    'K': 2, 'L': 4, 'M': 18, 'N': 20, 'O': 11, 'P': 3, 'Q': 6, 'R': 8, 'S': 12, 'T': 14,
    'U': 16, 'V': 10, 'W': 22, 'X': 25, 'Y': 24, 'Z': 23
  };
  
  let paritySum = 0;
  for (let i = 0; i < 15; i++) {
    const char = cfUpper.charAt(i);
    if ((i + 1) % 2 === 0) { // Pari (2nd, 4th...)
      paritySum += setPari[char] || 0;
    } else { // Dispari (1st, 3rd...)
      paritySum += setDispari[char] || 0;
    }
  }
  
  const checkDigit = String.fromCharCode((paritySum % 26) + 65);
  return checkDigit === cfUpper.charAt(15);
};

/**
 * Estrae consonanti e vocali da una stringa (pulita da accenti e caratteri speciali)
 */
const getParts = (str: string) => {
  const normalized = str.toUpperCase().replace(/[^A-Z]/g, '');
  const consonants = normalized.replace(/[AEIOU]/g, '');
  const vowels = normalized.replace(/[^AEIOU]/g, '');
  return { consonants, vowels };
};

/**
 * Calcola la parte del cognome (primi 3 caratteri)
 */
export const calcolaParteCognome = (cognome: string): string => {
  const { consonants, vowels } = getParts(cognome);
  const res = (consonants + vowels + 'XXX').substring(0, 3);
  return res;
};

/**
 * Calcola la parte del nome (primi 3 caratteri)
 */
export const calcolaParteNome = (nome: string): string => {
  const { consonants, vowels } = getParts(nome);
  if (consonants.length >= 4) {
    return consonants[0] + consonants[2] + consonants[3];
  }
  return (consonants + vowels + 'XXX').substring(0, 3);
};

/**
 * Verifica congruenza tra dati anagrafici e codice fiscale
 * cf: string 16 char
 * dataNascita: YYYY-MM-DD
 * sesso: M/F
 * cognome: string
 * nome: string
 * codiceBelfiore: string (opzionale se non l'abbiamo ancora)
 */
export const verificaCongruenzaCF = (
  cf: string, 
  dataNascita: string, 
  sesso: string, 
  cognome: string, 
  nome: string,
  codiceBelfiore?: string
) => {
  if (!cf || !dataNascita || !sesso || !cognome || !nome) return { valid: true };
  const cfUpper = cf.toUpperCase().trim();
  if (cfUpper.length !== 16) return { valid: false, message: 'Lunghezza Codice Fiscale errata' };
  
  if (!validaCodiceFiscale(cfUpper)) {
    return { valid: false, message: 'Codice Fiscale non valido formalmente o nel carattere di controllo' };
  }

  // 1. Cognome (1-3)
  const expectedCognome = calcolaParteCognome(cognome);
  if (cfUpper.substring(0, 3) !== expectedCognome) {
    return { valid: false, message: `Incongruenza Cognome: atteso ${expectedCognome}, trovato ${cfUpper.substring(0, 3)}` };
  }

  // 2. Nome (4-6)
  const expectedNome = calcolaParteNome(nome);
  if (cfUpper.substring(3, 6) !== expectedNome) {
    return { valid: false, message: `Incongruenza Nome: atteso ${expectedNome}, trovato ${cfUpper.substring(3, 6)}` };
  }

  // 3. Anno, Mese, Giorno/Sesso (7-11)
  const date = new Date(dataNascita);
  if (isNaN(date.getTime())) return { valid: true };
  
  const year = date.getFullYear().toString().substring(2);
  const months = ['A', 'B', 'C', 'D', 'E', 'H', 'L', 'M', 'P', 'R', 'S', 'T'];
  const monthChar = months[date.getMonth()];
  
  let day = date.getDate();
  if (sesso === 'F') day += 40;
  const dayStr = day.toString().padStart(2, '0');
  
  const cfDatePart = cfUpper.substring(6, 11);
  const expectedDatePart = `${year}${monthChar}${dayStr}`;
  
  if (cfDatePart !== expectedDatePart) {
    return { 
      valid: false, 
      message: `Incongruenza Data/Sesso: atteso ${expectedDatePart}, trovato ${cfDatePart}` 
    };
  }

  // 4. Comune (12-15)
  if (codiceBelfiore && codiceBelfiore.length === 4) {
    if (cfUpper.substring(11, 15) !== codiceBelfiore.toUpperCase()) {
      return { 
        valid: false, 
        message: `Incongruenza Comune: atteso ${codiceBelfiore}, trovato ${cfUpper.substring(11, 15)}` 
      };
    }
  }
  
  return { valid: true };
};
